import { useEffect } from "react";
import { Outlet } from "react-router-dom";
import { useAgentLocationTracker } from "@/features/agent/hooks/useAgentLocationTracker";
import { useAgentProfile } from "@/features/agent/hooks/useAgentMissions";
import { useAgentPayoutNotifier } from "@/features/agent/hooks/useAgentEarnings";
import { useAgentPresence } from "@/features/agent/hooks/useAgentPresence";
import { useKeepAwake } from "@/shared/platform/wakeLock";
import { OfferHost } from "@/features/agent/components/OfferHost";
import { useAgentStore } from "@/features/agent/stores/agentStore";
import { useDeviceRegistration } from "@/features/notifications/useDeviceRegistration";
import { useRealtimeResync } from "@/shared/realtime";
import { LocationGate } from "./LocationGate";
import { LocationPermissionDialog } from "./LocationPermissionDialog";
import { ensureAgentIdResolved } from "@/features/agent/stores/agentStore";

/**
 * Enveloppe de l'espace agent — port d'AgentStack.tsx.
 *
 * Ne rend aucune chrome (les écrans agent sont plein écran, comme en natif) :
 * son rôle est de porter les deux effets qui doivent vivre pendant TOUTE la
 * session agent, et non écran par écran.
 */
export function AgentShell() {
  // Résout l'agentId (profil /agents) DÈS l'entrée dans l'espace agent : sans
  // ça, useAgentActiveTrips/useAgentMissions restaient `disabled` (listes vides)
  // et le tracker GPS inerte jusqu'au premier passage en ligne — notamment
  // après un logout→login (clearSession vide sessionStore.agentId).
  useEffect(() => {
    void ensureAgentIdResolved();
  }, []);

  // Pousse la position GPS de l'agent vers POST /agents/{id}/location pendant
  // toute la session agent (accueil, mission, course…). Gardé en interne par le
  // statut en ligne (AVAILABLE/BUSY) ; le backend rediffuse via le socket
  // `agent.location.updated` aux clients qui suivent leur commande.
  //
  // ⚠️ Contrainte web absente du natif : le navigateur SUSPEND la géolocalisation
  // d'un onglet en arrière-plan, et une app web ne peut pas suivre la position
  // écran éteint. Un agent qui bascule d'onglet ou verrouille son téléphone
  // cesse d'émettre sa position — là où l'app native continuait en tâche de
  // fond. C'est une limite structurelle du web, pas un réglage : si le suivi
  // continu est indispensable au métier, l'app agent doit rester native ou
  // devenir une PWA installée (et même alors, le suivi écran éteint reste
  // impossible).
  // Statut agent resynchronisé DEPUIS LE SHELL, et non depuis l'accueil.
  //
  // Le store démarre à OFFLINE, et `useAgentLocationTracker` n'émet la position
  // que si le statut vaut AVAILABLE ou BUSY. Or la synchronisation ne vivait que
  // dans `AgentHomeScreen` : un agent qui rechargeait sa page sur une mission,
  // ou qui ouvrait un lien direct vers une course, ne passait jamais par
  // l'accueil. Son app se croyait hors ligne et n'émettait AUCUNE position —
  // pendant que lui voyait bien l'invite du navigateur et se croyait suivi.
  // Côté client, la carte restait sur « En attente de la position de la Main ».
  const agentProfile = useAgentProfile();
  const backendStatus = agentProfile.data?.status;
  const syncStatusFromProfile = useAgentStore((s) => s.syncStatusFromProfile);
  const agentStatus = useAgentStore((s) => s.status);
  useEffect(() => {
    if (backendStatus) syncStatusFromProfile(backendStatus);
  }, [backendStatus, syncStatusFromProfile]);

  // Réaffirme `AVAILABLE` au retour au premier plan et après une reprise
  // réseau. Sans cela, une Main éteinte par le serveur après quinze minutes
  // sans position ne recevrait plus jamais d'offre : `POST /location` ne touche
  // pas au statut, et reprendre le GPS ne la réactive pas.
  useAgentPresence();

  // Cf. AppShell : recharge les données après une reprise non rejouée.
  // Ré-enregistre l'appareil push à chaque démarrage et reprise réseau
  // (idempotent côté serveur, et sans jamais demander la permission).
  useDeviceRegistration();

  // Les issues de retrait suivent la Main partout, et pas seulement sur
  // l'écran des gains : un virement aboutit — ou échoue — pendant qu'elle est
  // en course, et le document impose alors de recharger ses gains ET de lui
  // montrer la phrase du serveur, qui ne repassera jamais.
  useAgentPayoutNotifier();

  useRealtimeResync();

  useAgentLocationTracker();

  // ── Empêcher l'écran de s'éteindre pendant le service ────────────────────
  //
  // Tant que la Main est en ligne, un écran qui s'éteint gèle la page : plus de
  // `watchPosition`, plus de battement, et le client regarde un marqueur
  // immobile sans savoir pourquoi. Le cas n'a rien d'exceptionnel — c'est ce
  // qui arrive quand elle pose son téléphone sur le guidon.
  //
  // ⚠️ Ce verrou ne couvre QUE l'écran qui s'éteint tout seul, page visible. Il
  // ne fait rien si la Main bascule sur une autre app ou verrouille son
  // téléphone : le navigateur relâche alors le verrou d'office. Aucune API web
  // ne permet de suivre une position page gelée — `navigator.geolocation`
  // n'existe pas dans un service worker. Cf. `shared/platform/wakeLock`.
  //
  // Même condition que le traqueur, et ce n'est pas un hasard : garder l'écran
  // allumé hors service viderait la batterie de la Main pour rien.
  const isOnDuty = agentStatus === "AVAILABLE" || agentStatus === "BUSY";
  useKeepAwake(isOnDuty);

  return (
    <>
      {/* Même exigence que côté client : sans position, un agent ne peut ni
          être proposé sur une mission, ni voir sa destination. Le blocage vaut
          donc pour les deux espaces. */}
      <LocationGate />
      <LocationPermissionDialog />
      {/* L'offre doit atteindre la Main où qu'elle soit : cinq minutes pour
          répondre, et le serveur la passe `BUSY` dès l'ÉMISSION — ignorer une
          offre l'immobilise jusqu'à expiration. */}
      <OfferHost />
      <Outlet />
    </>
  );
}
